import React from "react";
import ManualContent from "../../components/ManualContent";
import { MathJax } from "better-react-mathjax";
import { Link } from "react-router-dom";

const ModelList = () => {
  return (
    <ManualContent title="Available Experimental Models in Speqqle">
      <div id="top" />
      <p>
        Speqqle includes a library of <strong>experimental models</strong> that
        simulate and fit real-world optical measurements. These models are
        composed of one or more <strong>submodels</strong> (such as materials or
        layers) and define how your experiment interacts with those components.
      </p>

      <p>This section contains:</p>
      <ul>
        <li>
          A list of all <strong>available experimental models</strong>, each
          linked to a detailed explanation below.
        </li>
        <li>
          Followed by a dedicated section for each model, including:
          <ul>
            <li>What the model does and how it works</li>
            <li>Inputs and outputs</li>
            <li>When and why to use it</li>
            <li>Common mistakes to avoid</li>
            <li>A deep-dive into the underlying physics</li>
          </ul>
        </li>
        <hr />
      </ul>

      <h2>List of Experiments</h2>
      <ul>
        <li>
          <a href="#rtamodel">
            {" "}
            &quot;Isotropic R/T/A/Ellips&quot;: Fit reflectivity, transmission, absorption and
            Ellipsometric observables of isotropic (multilayer) samples. {" "}
          </a>
        </li>
             <li>
          <a href="#animodel">
            {" "}
            &quot;Anisotropic R/T/A/&quot;: Fit reflectivity and transmission of orthorombic (multilayer) samples.{" "}
          </a>
        </li>
                {/* NEW: Differential model in the list */}
        <li>
          <a href="#diffmodel">
            {" "}
            &quot;COMING SOON: Differential R/T&quot;: Fit small relative changes{" "}
            {" \u0394R/R "} and {" \u0394T/T "} due to small
            perturbations in one or more layers.{" "} Please contact an admin if you want early access to this model.
            {" "}
          </a>
        </li>
        <li>
          <a href="#ramanmodel">
            {" "}
            &quot;Raman&quot;: Fit Stokes and anti Stokes for Raman thermometry.
          {" "}
          </a>
        </li>
        <li>
          <a href="#PDmodelbulk">
            {" "}
            &quot;SNOM: Bulk Point Dipole&quot;: Fit near field scattering spectra. 
             {" "}
            </a>
        </li>
        <li>
          <a href="#FDmodelbulk">
            {" "}
            &quot;SNOM: Bulk Finite dipole &quot;: Fit near field scattering spectra using the finite dipole formalism. 
            {" "}
          </a>
        </li>
        <li>
          <a href="#THzmodel">
            {" "}
            &quot;TeraHertz time domain spectroscopy &quot;: Fit the time domain spectra directly with a single or multilayer material model.
            {" "}
          </a>
        </li>
        <li>
          <a href="#Gasmodel">
           {" "}
            &quot;NEW: Gas absorption spectroscopy &quot;: Fit emission and absorption using a Voight model
            {" "}
            </a>
        </li>
        <li>
          <a href="#PDmodel">Coming soon: SNOM: Point Dipole Model for multilayer samples and thin films</a>
        </li>
        <li>
          <a href="#FDmodel">Coming soon: SNOM: Finite Dipole Model for multilayer samples and thin films</a>
        </li>
        <hr />
      </ul>

      
      {/* R/T/A/Ellips Section */}
      <MathJax>
        <section
          id="rtamodel"
          style={{ marginTop: "3rem", marginBottom: "3rem" }}
        >
          <h3>
            &quot;Isotropic R/T/A/Ellips&quot;: Reflectivity / Transmission / Absorption / Ellipsometry
          </h3>
          <h4>
            &#8594; Fit <strong>isotropic</strong> bulk, thin films, thin films on a substrate or multilayer structures. 
          </h4>

          {/* Model Description */}
          <p>
            This model simulates and fits light–matter interactions through isotropic thin
            films, multilayers, or isotropic bulk materials, predicting optical responses
            such as <strong>Reflectivity</strong>, <strong>Transmission</strong>
            , <strong>Absorption</strong>, and <strong>Ellipsometry</strong>. It
            is ideal for extracting material properties and layer thicknesses in
            layered structures.
          </p>

          {/* Inputs Table */}
          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td> Material Optical Response </td>
                <td>
                  Each layer must reference a <strong>Material submodel</strong>{" "}
                  providing the complex dielectric function{" "}
                  {"\\( \\varepsilon(\\omega) \\)"}.{" "}
                </td>
                <td>unitless</td>
              </tr>
              <tr>
                <td>Layer Thickness</td>
                <td>
                  Physical thickness of each material layer, including substrate
                </td>
                <td>cm</td>
              </tr>
              <tr>
                <td>Coherence (&quot;coh&quot;)</td>
                <td>
                  Determines whether <strong>interference effects</strong>{" "}
                  (e.g., Fabry-Pérot) are modeled in a given layer.
                </td>
                <td>1 = coherent, 0 = incoherent</td>
              </tr>
              <tr>
                <td>Angle of Incidence {"\\( \\theta \\)"}</td>
                <td>Incident angle of the light beam on the sample surface</td>
                <td>degrees</td>
              </tr>
              <tr>
                <td>Polarizer Angle (&quot;pol&quot;)</td>
                <td>Angle of linear polarization</td>
                <td>degrees</td>
              </tr>
            </tbody>
          </table>

          {/* Outputs Table */}
          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( R(\\omega) \\)"}</td>
                <td>
                  Reflectivity: power ratio of reflected to incident light
                </td>
                <td>unitless (0–1)</td>
                <td>Reflection</td>
              </tr>
              <tr>
                <td>{"\\( T(\\omega) \\)"}</td>
                <td>
                  Transmittivity: power ratio of transmitted to incident light
                </td>
                <td>unitless (0–1)</td>
                <td>Transmission</td>
              </tr>
              <tr>
                <td>{"\\( A(\\omega) \\)"}</td>
                <td>Absorption, computed as {"\\( A = 1 - R - T \\)"}</td>
                <td>unitless (0–1)</td>
                <td>All</td>
              </tr>
              <tr>
                <td>{"\\( \\Psi \\)"}</td>
                <td>
                  Ellipsometric amplitude ratio:{" "}
                  {"\\( \\tan(\\Psi) = |r_p / r_s| \\)"}
                </td>
                <td>degrees</td>
                <td>
                  Reflection, and transmission when {"\\( r \\)"} is subsituted
                  by {"\\( t \\)"}
                </td>
              </tr>
              <tr>
                <td>{"\\( \\Delta \\)"}</td>
                <td>
                  Ellipsometric phase shift:{" "}
                  {"\\( \\Delta = \\arg(r_p / r_s) \\)"}. Speqqle assumes{" "}
                  {"\\( \\Delta = 180^\\circ - \\Delta_\\text{measured} \\)"} by
                  default.
                </td>
                <td>degrees</td>
                <td>
                  Reflection, and transmission when {"\\( r \\)"} is subsituted
                  by {"\\( t \\)"}
                </td>
              </tr>
              <tr>
                <td>{"\\( \\alpha \\)"}</td>
                <td>
                  Amplitude ratio for analyzer modulation without compensator
                  (Fourier coefficient)
                </td>
                <td>unitless</td>
                <td>Reflection and transmission</td>
              </tr>
              <tr>
                <td>{"\\( \\beta \\)"}</td>
                <td>
                  Phase difference for analyzer modulation without retarder
                  (Fourier coefficient, depends on {"\\( \\Psi,\\ \\Delta \\)"})
                </td>
                <td>unitless</td>
                <td>Reflection and transmission</td>
              </tr>
            </tbody>
          </table>

          {/* Use Cases */}
          <h4>Use Cases</h4>
          <ul>
            <li>
              Extract dielectric functions or refractive indices from real-world
              spectra
            </li>
            <li>
              Determine thin-film or multilayer thicknesses non-destructively
            </li>
            <li>
              Analyze changes in surface coatings, oxide growth, or buried
              interfaces
            </li>
            <li>
              Study interference patterns and Fabry–Pérot resonances in optical
              stacks
            </li>
          </ul>

          {/* Setup Types */}
          <h4>Experiment Setup</h4>
          <ul>
            <li>
              <strong>Single-layer (bulk or thick slab):</strong>
              Use a single <strong>Material</strong> model with a large
              thickness (e.g., &gt; 1 - 10 cm or even more). You may choose to
              treat this layer as <strong>coherent</strong> (to capture
              Fabry-Pérot oscillations) or <strong>incoherent</strong> (to
              ignore internal interference). This setup is suitable for
              crystals, wafers, or thick films.
            </li>
            <li>
              <strong>Thin film on substrate:</strong>
              Define two layers: the thin film (coherent) and the substrate
              (often incoherent if thick). be sure to include experimental data
              of the substrate and the film on the substrate in your analysis.
              This setup is ideal for reflectivity and ellipsometry on coated
              samples.
            </li>
            <li>
              <strong>Multilayer stack:</strong>
              Add three or more layers (e.g., multiple coatings or
              heterostructures). Specify thickness and coherence for each.
              Interference between coherent layers produces Fabry-Pérot effects,
              while incoherent layers average out internal phases. Be sure to
              include the substrate in the optical stack.
            </li>
          </ul>

          {/* Warnings */}
          <MathJax>
            <h4>Points of Attention</h4>
            <ul>
              <li>
                <strong>Layer definitions:</strong> Ensure every layer includes
                a valid
                <strong> Material submodel </strong> and a realistic thickness
                value in cm.
              </li>
              <li>
                <strong>Coherence setting:</strong> Use the <code>coh</code>{" "}
                flag to indicate whether a layer is <strong>coherent</strong>{" "}
                (phase preserved) or <strong>incoherent</strong>
                (no phase memory). Coherent layers allow for Fabry–Pérot
                interference.
              </li>
              <li>
                <strong>Fabry–Pérot effects:</strong> Even a single slab of
                material can exhibit Fabry–Pérot oscillations if treated as
                coherent. Set <code>coh = 1</code> if you want to model internal
                interference.
              </li>
              <li>
                <strong>Angle and polarization:</strong> Make sure the angle of
                incidence
                {"\\( \\theta \\)"} and polarizer angle (&quot;pol&quot;) match
                your experimental setup. For somewhat curved samples, try
                fitting the angle.
              </li>
              <li>
                <strong>
                  Ellipsometric phase shift ({"\\( \\Delta \\)"}):
                </strong>{" "}
                Speqqle assumes ellipsometers <em>without</em> a compensator. It
                therefore uses:
                <br />
                <span style={{ marginLeft: "1em" }}>
                  {"\\( \\Delta = 180^\\circ - \\Delta_\\text{measured} \\)"}
                </span>
                <br />
                If your values appear inverted, adjust this during data import
                or preprocessing.
              </li>
              <li>
                <strong>Unit consistency:</strong> Use centimeters for thickness
                and {"\\( \\text{cm}^{-1} \\)"} for optical frequency{" "}
                {"\\( \\omega \\)"} to match Speqqle&apos;s internal unit
                system.
              </li>
            </ul>
          </MathJax>

          {/* Advanced Physics (Collapsible) */}
          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
              Light propagation is computed using the transfer matrix method,
              solving Maxwell’s equations at every interface in the structure.
            </p>
            <p>
              Fresnel equations determine reflectance and transmittance at each
              boundary, including interference and polarization effects.
            </p>
            <p>
              For Ellipsometry:
              {"\\[ \\rho = \\frac{r_p}{r_s} = \\tan(\\Psi) e^{i\\Delta} \\]"}
            </p>
            <p>
              Speqqle internally uses the measured angle conversion:
              {"\\[ \\Delta = 180^\\circ - \\Delta_\\text{measured} \\]"}
            </p>
          </details>

          {/* Back to Top */}
          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </section>
      </MathJax>

       {/* Anisotropy Section */}
      <MathJax>
        <section
          id="animodel"
          style={{ marginTop: "3rem", marginBottom: "3rem" }}
        >
          <h3>
            &quot;Anisotropic R/T/A &quot;: Reflectivity / Transmission / Absorption of an anisotropic sample
          </h3>
          <h4>
            &#8594; Fit <strong>biaxial anisotropic</strong> bulk, thin films, thin films on a substrate or multilayer structures. 
          </h4>

          {/* Model Description */}
          <p>
         This model simulates light propagation and reflection in multilayer
      systems composed of <strong>anisotropic materials</strong> described by a
      diagonal dielectric tensor{" "}
      {"\\( \\varepsilon = \\text{diag}(\\varepsilon_x, \\varepsilon_y, \\varepsilon_z) \\)"}. 
      It supports arbitrary angle of incidence within the <strong>x–z
      plane</strong> and a variable polarization angle. The model computes the
      <strong> reflectivity, transmission, and absorption</strong> spectra for
      arbitrary polarization states but does not yet calculate ellipsometric
      observables such as {"\\( \\Psi,  \\text{or }  \\Delta \\)"}.
          </p>

            <p>
      The geometry corresponds to an incident wavevector{" "}
      {"\\( \\mathbf{k} \\)"} lying in the x–z plane. The polarization angle
      defines the orientation of the incident electric field between s- and
      p-polarization. As a result:
    </p>
    <ul>
      <li>
        Polarization 0° (p-polarized) probes {" "}
        {"\\( \\varepsilon_x \\)"} and {"\\( \\varepsilon_z \\)"}.
      </li>
      <li>
        Polarization 90° (s-polarized) probes {" "}
        {"\\( \\varepsilon_y \\)"}.
      </li>
      <li>
        Intermediate polarization angles probe mixed contributions of all three
        tensor components.
      </li>
    </ul>

          <h4>Limitations of this model</h4>
          <p>
            The model assumes no off-diagonal tensor components and fixed principal axes (no rotation).
          </p>

          {/* Inputs Table */}
          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td> Material Optical Response </td>
                <td>
                  Each layer must reference three <strong>Material submodels</strong> that
            together define the anisotropic dielectric tensor components{" "}
            {"\\( \\varepsilon_x(\\omega), \\varepsilon_y(\\omega), \\varepsilon_z(\\omega) \\)"}.
                </td>
                <td>unitless</td>
              </tr>
              <tr>
                <td>Layer Thickness</td>
                <td>
                 Physical thickness of each layer in the stack
                </td>
                <td>cm</td>
              </tr>
              <tr>
                <td>Coherence (&quot;coh&quot;)</td>
                <td>
                  Determines whether <strong>interference effects</strong>{" "}
                  (e.g., Fabry-Pérot) are modeled for that layer.
                </td>
                <td>1 = coherent, 0 = incoherent</td>
              </tr>
              <tr>
                <td>Angle of Incidence {"\\( \\theta \\)"}</td>
                <td>Incident angle of the light beam, measured from the surface normal
            in the x–z plane.</td>
                <td>degrees</td>
              </tr>
              <tr>
                <td>Polarizer Angle (&quot;pol&quot;)</td>
                <td>Angle of the electric field vector relative to the x-axis.
            <br />
            0° = p-polarization, 90° = s-polarization.</td>
                <td>degrees</td>
              </tr>
            </tbody>
          </table>

          {/* Outputs Table */}
          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( R(\\omega) \\)"}</td>
                <td>
                  Reflectivity: power ratio of reflected to incident light, {"\\( R(\\omega) = R_{ss} \\cos^2(pol) + R_{pp} \\sin^2(pol) \\)"}
                </td>
                <td>unitless (0–1)</td>
                <td>Reflection</td>
              </tr>
              <tr>
                <td>{"\\( T(\\omega) \\)"}</td>
                <td>
                  Transmission: ratio of transmitted to incident intensity, {"\\( T(\\omega) = T_{ss} \\cos^2(pol) + T_{pp} \\sin^2(pol) \\)"}
                </td>
                <td>unitless (0–1)</td>
                <td>Transmission</td>
              </tr>
              <tr>
                <td>{"\\( A(\\omega) \\)"}</td>
                <td>Absorption, computed as {"\\( A = 1 - R - T \\)"}</td>
                <td>unitless (0–1)</td>
                <td>All</td>
              </tr>
            </tbody>
          </table>

          {/* Use Cases */}
          <h4>Use Cases</h4>
          <ul>
            <li>
              Fit or simulate spectra of anisotropic materials, such as crystals or
        oriented films, where optical properties differ along x, y, and z.
            </li>
            <li>
               Analyze polarization-dependent reflectivity and transmission to extract
        anisotropic dielectric tensor components.
            </li>
            <li>
             Study birefringence and dichroism effects in structured thin films or
        layered crystals.
            </li>
            <li>
              Study interference patterns and Fabry–Pérot resonances in optical
              stacks with anisotropic materials.
            </li>
          </ul>

          {/* Setup Types */}
          <h4>Experiment Setup</h4>
          <ul>
            <li>
              <strong>Bulk anisotropic sample:</strong> Use three material models
        and a large thickness (e.g., &gt; 1 cm). The model will simulate
        reflection and transmission at variable polarization and angle of incidence.
            </li>
            <li>
              <strong>Thin film on substrate:</strong> Combine an anisotropic film
        (coherent) with an isotropic or anisotropic substrate (often incoherent
        if thick). Use multiple polarization angles for accurate tensor fitting.
            </li>
            <li>
              <strong>Multilayer stack:</strong> For complex heterostructures or
        superlattices, assign anisotropic material models to each layer. Each
        layer may have different tensor elements and coherence settings.
            </li>
          </ul>

          {/* Warnings */}
          <MathJax>
            <h4>Points of Attention</h4>
            <ul>
              <li>
        The model assumes a diagonal dielectric tensor aligned with the sample
        axes. Optical axis rotation or off-diagonal tensor components are not
        included.
      </li>
              <li>
        Light always propagates in the x–z plane. Oblique incidence is allowed,
        but the model does not handle wavevectors in arbitrary 3D directions.
      </li>
      <li>
        Polarization is defined in the y–z plane, allowing smooth transitions
        between pure s and pure p polarizations.
      </li>
      <li>
            <strong>Isotropic layers:</strong> to include one or more isotropic layers, set the three
            <strong> Material submodels </strong> of that layer the same.
      </li>
      <li>
        Ensure correct layer thickness and coherence settings to avoid
        artificial interference patterns.
      </li>
      <li>
        Units: thickness in cm, frequency in{" "}
        {"\\( \\text{cm}^{-1} \\)"}, angles in degrees.
      </li>
            </ul>
          </MathJax>

          {/* Advanced Physics (Collapsible) */}
          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
        Light propagation is computed using an extended transfer matrix formalism
        for anisotropic media, solving Maxwell’s equations for each layer and
        polarization configuration. The model treats the electric field
        components parallel and perpendicular to the optical axes independently.
      </p>
            <p>
        The reflection and transmission coefficients are derived from boundary
        conditions of the electromagnetic fields at each interface:
      </p>
      <MathJax>
        {`
        \\[
          \\mathbf{r}, \\mathbf{t} = f(\\varepsilon_x, \\varepsilon_y, \\varepsilon_z, \\theta, \\text{pol})
        \\]
        `}
      </MathJax>
      <p>
  For <strong>p-polarization</strong>, pol = 0°, the electric field lies in the plane of incidence (x–z), coupling to both {"\\( \\varepsilon_x \\)"} and {"\\( \\varepsilon_z \\)"}.
  <br />
  For <strong>s-polarization</strong>, pol = 90°, the electric field is perpendicular to the plane of incidence and points along the y-axis, thus interacting <strong>only</strong> with {"\\( \\varepsilon_y \\)"} — even at oblique incidence.
  <br />
  Intermediate polarization angles probe linear combinations of all relevant tensor components. However, note that {"\\( \\varepsilon_z \\)"} only contributes when the electric field has a component along z (i.e., for non-s-polarized light).
</p>

        <p>
        Since the optical axes are fixed to the sample coordinates, the model is
        best suited for orthorhombic or uniaxial crystals aligned with the
        measurement frame.
      </p>
          </details>

          {/* Back to Top */}
          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </section>
      </MathJax>
      

      {/* Raman Section */}
      <section
        id="ramanmodel"
        style={{ marginTop: "3rem", marginBottom: "3rem" }}
      >
        <MathJax>
          <h3>Raman and Raman thermometry</h3>

          <p>
            This model fits and simulates{" "}
            <strong>Raman scattering spectra</strong>, including both
            <strong> Stokes</strong> and <strong>anti-Stokes</strong> sides. It
            allows for extraction of phonon mode energies and lifetimes and
            enables <strong>non-contact temperature estimation</strong> through
            Raman thermometry.
          </p>

          {/* Inputs Table */}
          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( \\omega_0 \\)"}</td>
                <td>Resonance frequency of the phonon or vibrational mode</td>
                <td>
                  cm<sup>-1</sup>
                </td>
              </tr>
              <tr>
                <td>{"\\( A \\)"}</td>
                <td>Oscillator amplitude</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>{"\\( \\gamma \\)"}</td>
                <td>Linewidth (FWHM) of the phonon mode</td>
                <td>
                  cm<sup>-1</sup>
                </td>
              </tr>

              <tr>
                <td>{"\\( Asymm \\)"}</td>
                <td>
                  Asymmetry parameter in the Breit Wigner Fano line shape.
                  Corresponds to {"\\( 1/q \\)"}. The excitation is symmatric
                  when Asymm = 0.
                </td>
                <td> - </td>
              </tr>
              <tr>
                <td>Laser frequency</td>
                <td>Frequency of excitation laser</td>
                <td>nm</td>
              </tr>
              <tr>
                <td> Constant corr, Lin corr, Quad corr</td>
                <td>
                  Camera correction coefficients: C( {"\\( \\omega \\)"})=
                  Constant corr + Lin corr {"\\( \\omega + \\)"} Quad corr{" "}
                  {"\\( \\omega^2 \\)"}. Keep Lin corr and Quad corr fixed to 0
                  unless you know what you are doing.
                </td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td> abs coeff</td>
                <td>
                  frequency independent absorption correction for high-T thermal
                  background: {"\\( I \\propto \\)"} abs coeff{" "}
                  {"\\( B(\\omega, T) \\)"}, where {"\\( B(\\omega, T) \\)"} is
                  Planck&apos;s law
                </td>
                <td>varies</td>
              </tr>
            </tbody>
          </table>

          {/* Outputs Table */}
          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Stokes spectrum</td>
                <td>Raman intensity for Stokes-shifted scattering</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>Anti-Stokes spectrum</td>
                <td>Raman intensity for anti-Stokes scattering</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>Fitted parameter: local temperature</td>
                <td>Extracted from Stokes/anti-Stokes ratio</td>
                <td>K</td>
              </tr>
              <tr>
                <td>
                  Other fitted parameters:{" "}
                  {"\\( \\omega_0 , A, \\gamma, Asymm \\)"}
                </td>
                <td>Extracted from Stokes and anti-Stokes ratio spectrum </td>
                <td>
                  cm<sup>-1</sup>, arb units, cm<sup>-1</sup>, arb units{" "}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Use Cases */}
          <h4>Use Cases</h4>
          <ul>
            <li>
              Fit phonon peaks in Raman spectra using Breit-Wigner-Fano or
              Lorentz line shapes (For Asymm = 0)
            </li>
            <li>
              Extract vibrational frequencies and linewidths from bulk or
              thin-film Raman data
            </li>
            <li>
              Perform <strong>Raman thermometry</strong> by fitting the
              anti-Stokes to Stokes ratio
            </li>
            <li>
              Apply intensity corrections for detector response and thermal
              emission at high temperatures
            </li>
          </ul>

          {/* Setup */}
          <h4>Experiment Setup</h4>
          <ul>
            <li>
              <strong>Stokes-only:</strong> Standard Raman spectra with positive
              frequency shift; used for mode identification.
            </li>
            <li>
              <strong>Stokes + Anti-Stokes:</strong> Required for Raman
              thermometry. Ensure symmetry and background handling is accurate.
            </li>
            <li>
              <strong>High-temperature applications:</strong> Include camera and
              absorption corrections to avoid overestimating intensity.
            </li>
          </ul>

          {/* Points of Attention */}
          <h4>Points of Attention</h4>
          <ul>
            <li>
              Ensure input units (e.g., cm⁻¹ for frequencies) match throughout
              the model.
            </li>
            <li>
              Anti-Stokes intensity is sensitive to noise and background —
              proper baseline subtraction is essential.
            </li>
            <li>
              Use camera correction{" "}
              {"\\( C(\\omega) = a \\omega + b \\omega^2 \\)"} to account for
              detector sensitivity, but only if you really know what you are
              doing. We suggest keeping them fixed and zero.
            </li>
            <li>
              If thermal radiation dominates (e.g. at high T), enable absorption
              scaling:
              {
                "\\( I_{\\text{obs}} \\propto \\alpha(\\omega) B(\\omega, T) \\)"
              }
            </li>
            <li>
              For accurate temperature extraction, the system should be in
              thermal equilibrium and free from laser-induced heating artifacts.
            </li>
          </ul>

          {/* Physics Block */}
          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
              Raman scattering occurs when photons interact with vibrational
              modes. The scattered light is shifted in energy by{" "}
              {"\\( \\pm \\hbar\\omega \\)"}:
            </p>
            <p style={{ marginLeft: "1em" }}>
              {
                "\\[ E_{\\text{scattered}} = E_{\\text{laser}} \\pm \\hbar\\omega \\]"
              }
            </p>
            <p>The phonon population follows the Bose-Einstein distribution:</p>
            <p style={{ marginLeft: "1em" }}>
              {"\\[ n(\\omega) = \\frac{1}{e^{\\hbar\\omega / k_B T} - 1} \\]"}
            </p>
            <p>The resulting Raman intensity is:</p>
            <ul>
              <li>
                Stokes: {"\\( I \\propto [n(\\omega) + 1] \\chi''(\\omega) \\)"}
              </li>
              <li>
                Anti-Stokes: {"\\( I \\propto n(\\omega) \\chi''(\\omega) \\)"}
              </li>
            </ul>
            <p>Temperature can be extracted from the intensity ratio:</p>
            <p style={{ marginLeft: "1em" }}>
              {
                "\\[ \\frac{I_{AS}}{I_S} = e^{-\\hbar\\omega / k_B T} \\quad \\Rightarrow \\quad T = \\frac{\\hbar\\omega}{k_B \\ln(I_S / I_{AS})} \\]"
              }
            </p>
            <p>The model optionally applies:</p>
            <ul>
              <li>
                Camera correction: C( {"\\( \\omega \\)"})= Constant corr + Lin
                corr {"\\( \\omega + \\)"} Quad corr {"\\( \\omega^2 \\)"}
              </li>
              <li>
                Thermal correction: {"\\( I \\propto \\)"} abs coeff{" "}
                {"\\( B(\\omega, T) \\)"}
              </li>
            </ul>
            <p>
              These enable realistic simulation and analysis, especially in
              high-temperature or low-signal environments.
            </p>
          </details>

          {/* Back to Top */}
          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </MathJax>
      </section>


     {/* Point Dipole Section - Updated to match manual style */}
     <section
        id="PDmodelbulk"
        style={{ marginTop: "3rem", marginBottom: "3rem" }}
      >
        <MathJax>
          <h3>SNOM: Bulk Point Dipole Model</h3>

          <p>
            This model simulates and fits near-field optical measurements performed on
            bulk samples using a scattering-type scanning near-field optical
            microscope (s-SNOM). It models the interaction between the tip and
            the sample using a point dipole approximation. The output includes
            signal amplitude and phase as measured by a typical SNOM setup and normalized to a perfect reference.
          </p>

          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( a \\)"}</td>
                <td>Tip radius (radius of the point dipole)</td>
                <td>nm</td>
              </tr>
              <tr>
                <td>{"\\( z_0 \\)"}</td>
                <td>Minimal tip-to-sample distance</td>
                <td>nm</td>
              </tr>
              <tr>
                <td>{"\\( z_{amp} \\)"}</td>
                <td>Tapping amplitude of the oscillating tip</td>
                <td>nm</td>
              </tr>
              <tr>
                <td>{"\\( N_{harm} \\)"}</td>
                <td>Number of harmonics used in the signal calculation</td>
                <td> integer</td>
              </tr>
              <tr>
                <td>{"\\( N_{harm}^{High} \\)"}</td>
                <td>Number of a higher harmonics used in the signal calculation when a higher Harmonic is used as a reference signal</td>
                <td> integer</td>
              </tr>
              <tr>
                <td>{"\\( NFT \\)"}</td>
                <td>Number of Fourier transform samples for tip modulation</td>
                <td> integer</td>
              </tr>
              <tr>
                <td>{"\\( FF   cor \\)"}</td>
                <td>Far-field correction on or off (unchecked: disabled)</td>
                <td>-</td>
              </tr>
              <tr>
                <td>{"\\( FF   coef \\)"}</td>
                <td>Far-field correction coefficient (0 disables it)</td>
                <td>-</td>
              </tr>
              <tr>
                <td>{"\\( \\alpha \\)"}</td>
                <td>Angle of incidence for the far-field correction</td>
                <td>degrees</td>
              </tr>
              <tr>
                <td>Material Model</td>
                <td>Linked dielectric function of the bulk sample, usually Drude-Lorentz</td>
                <td>-</td>
              </tr>
            </tbody>
          </table>

          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( S(\\omega) \\)"}</td>
                <td>Near-field amplitude signal of the {"\\( N_{Harm} \\)"} Harmonic</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>{"\\( P(\\omega) \\)"}</td>
                <td>Near-field phase signal</td>
                <td>degrees or radians</td>
              </tr>
              <tr>
              <td>{"\\( S(\\omega) \\)"}</td>
                <td>Near-field amplitude signal of the {"\\(  N_{Harm} \\)"} Harmonic</td>
                <td>arb. units</td>
              </tr>
            </tbody>
          </table>

          <h4>Use Cases</h4>
          <ul>
            <li>Fitting near-field reflectivity or contrast data in s-SNOM</li>
            <li>Validating far-field dielectric functions using near-field data</li>
            <li>Quantifying dielectric properties at the nanoscale</li>
          </ul>

          <h4>Points of Attention</h4>
          <ul>
            <li>
              Always link a valid material model. This model calculates its
              output based on the material dielectric function
              {"\\( \\varepsilon(\\omega) \\)"}.
            </li>
            <li>
              To disable the far-field correction, set the correction coefficient
              to {"\\( FF coef = 0 \\)"} or uncheck the FF cor check-box.
            </li>
            <li>
              The number of FFT points and harmonics must be sufficiently high to
              avoid aliasing artifacts.
            </li>
            <li>
              The tip radius and tapping amplitude significantly influence the
              signal&apos;s depth sensitivity, always set them to the known values of the experiment and avoid fitting them in a first approach.
            </li>
          </ul>

          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
              The model solves a simplified dipole–sample interaction, assuming a
              vertically modulated tip and evaluating the time-dependent
              reflection coefficient using Fourier transforms.
            </p>
            <MathJax>
              {`
              \\[
                \\beta = \\frac{\\varepsilon - 1}{\\varepsilon + 1}, \\quad
                \\alpha(z) = \\frac{a^3}{1 - \\beta \\cdot \\frac{a^3}{4z^3}}, \\quad
                S(\\omega) = \\left| \\sum_n \\alpha(z_n) e^{i n \\phi_n} \\right|, \\quad
                P(\\omega) = \\arg\\left( \\sum_n \\alpha(z_n) e^{i n \\phi_n} \\right)
              \\]
              `}
            </MathJax>
            <p>
              where {"\\( z_n = z_0 + a + z_{amp}(1 + \\cos \\phi_n) \\)"} varies
              during tapping. The signal is obtained from the nth harmonic of the
              time-domain dipole response.
            </p>
          </details>

          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </MathJax>
      </section>


      <section
  id="FDmodelbulk"
  style={{ marginTop: "3rem", marginBottom: "3rem" }}
>
  <MathJax>
    <h3>SNOM: Bulk Finite Dipole Model</h3>

    <p>
      This model simulates and fits near-field optical signals from bulk
      samples using a finite dipole approximation, as originally proposed
      by Lewin. It improves upon the point dipole model by better
      representing the elongated geometry of the SNOM tip and the
      resulting electrostatic field distributions. This is particularly
      relevant for quantitative analysis of scattering-type near-field
      microscopy (s-SNOM) experiments.
    </p>

    <h4>Inputs</h4>
    <table className="manual-table">
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Description</th>
          <th>Units</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{"\\( a \\)"}</td>
          <td>Tip radius of the dipole</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( z_0 \\)"}</td>
          <td>Minimal tip–sample distance</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( z_{amp} \\)"}</td>
          <td>Tapping amplitude of the oscillating tip</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( L \\)"}</td>
          <td>Effective tip length (default: 600 nm)</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( N_{harm} \\)"}</td>
          <td>Harmonic used for signal calculation</td>
          <td>integer</td>
        </tr>
        <tr>
        <td>{"\\( N_{harm}^{High} \\)"}</td>
          <td>the higher Harmonic used for signal calculation</td>
          <td>integer</td>
        </tr>
        <tr>
          <td>{"\\( NFT \\)"}</td>
          <td>Fourier transform sampling points</td>
          <td>integer</td>
        </tr>
        <tr>
          <td>{"\\( FF\\ cor \\)"}</td>
          <td>Enable or disable far-field correction (checkbox)</td>
          <td>-</td>
        </tr>
        <tr>
          <td>{"\\( FF\\ coef \\)"}</td>
          <td>Far-field correction coefficient</td>
          <td>-</td>
        </tr>
        <tr>
          <td>{"\\( \\alpha \\)"}</td>
          <td>Angle of incidence</td>
          <td>degrees</td>
        </tr>
        <tr>
          <td>Material Model</td>
          <td>Linked bulk dielectric function (e.g. Drude–Lorentz)</td>
          <td>-</td>
        </tr>
      </tbody>
    </table>

    <h4>Outputs</h4>
<table className="manual-table">
  <thead>
    <tr>
      <th>Output</th>
      <th>Description</th>
      <th>Units</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>{"\\( S_{Harm}(\\omega) \\)"}</td>
      <td>Amplitude normalized to the reference at {"\\( N_{Harm} \\)"} </td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( P_{Harm}(\\omega) \\)"}</td>
      <td>Phase normalized to the reference at {"\\( N_{Harm} \\)"}</td>
      <td>radians</td>
    </tr>
    <tr>
      <td>{"\\( S(\\omega)^{High Harmonic}/S(\\omega)^{Harm} \\)"}</td>
      <td>Amplitude normalized to the Amplitude of the {"\\( N_{Harm} \\)"} signal. Here there is no prior normalization to the reference signal for both {"\\( S(\\omega) \\)"}</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( P(\\omega)^{High Harmonic}/P(\\omega)^{Harm} \\)"}</td>
      <td>Phase normalized to the Phase of the {"\\( N_{Harm} \\)"} signal. Here there is no prior normalization to the reference signal for both {"\\( P(\\omega) \\)"}</td>
      <td>radians</td>
    </tr>
    <tr>
      <td>{"\\( S(\\omega)^{High Harmonic} \\)"}</td>
      <td>Amplitude normalized to the reference, both at {"\\( N_{Harm}^{High} \\)"}</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( P(\\omega)^{High Harmonic} \\)"}</td>
      <td>Phase normalized to the reference, both at {"\\( N_{Harm}^{High} \\)"}</td>
      <td>radians</td>
    </tr>
    <tr>
      <td>{"\\( \\text{Re}[\\eta_{sam}(\\omega) / \\eta_{ref}(\\omega)] \\)"}</td>
      <td>Real part of the complex signal normalized to the reference for {"\\( N_{Harm} \\)"}</td>
      <td>arb. units</td>
    </tr>
    <tr>
      <td>{"\\( \\text{Im}[\\eta_{sam}(\\omega) / \\eta_{ref}(\\omega) ] \\)"}</td>
      <td>Imaginary part of the complex signal normalized to the reference for {"\\( N_{Harm} \\)"}</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( \\text{Abs}[\\eta_{sam}(\\omega)] \\)"}</td>
      <td>Amplitude of the sample signal for {"\\( N_{Harm} \\)"}, not normalized to the reference</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( \\text{Arg}[\\eta_{sam}(\\omega)] \\)"}</td>
      <td>Phase of the signal for {"\\( N_{Harm} \\)"}, not normalized by the reference</td>
      <td>radians</td>
    </tr>
    </tbody>
</table>


    <h4>Use Cases</h4>
    <ul>
      <li>
        Simulating and fitting s-SNOM spectra with improved tip–sample
        interaction physics taking the dielectric properties of the bulk sample into account. 
      </li>
      <li>
        Quantitative extraction of dielectric properties from near-field
        data.
      </li>
      <li>
        Cross-validation of far-field and near-field optical constants.
      </li>
    </ul>

    <h4>Points of Attention</h4>
    <ul>
      <li>
        Always use a well-constructed dielectric model for the material.
      </li>
      <li>
        If the far-field correction is not needed, disable it using the
        checkbox or set the correction coefficient to zero.
      </li>
      <li>
        Ensure the tip radius and tapping amplitude are set realistically
        from your experiment to start with. We have noticed that fitting the tip radius can improve results significantly and concluded that the tip is often more blunt than expected. 
      </li>
      <li>
        Do not overfit geometric parameters unless you have an
        experimental reason.
      </li>
      <li>
  The tip length <MathJax inline>{"\\( L \\)"}</MathJax> is typically fixed to a realistic value (e.g., 600 nm) to represent the effective height of the SNOM tip. This length determines the spacing between the two image dipoles used in the finite dipole approximation. If <MathJax inline>{"\\( L \\)"}</MathJax> is set too large, the two dipoles become too widely separated, which artificially enhances far-field contributions and distorts the near-field signal. This can lead to an overestimation of background effects and reduce spatial resolution. Fit <MathJax inline>{"\\( L \\)"}</MathJax> only with care, and constrain it to values consistent with the physical tip geometry.
</li>
    </ul>

    <details style={{ marginTop: "1rem" }}>
      <summary>
        <strong>Advanced: Underlying Physics</strong>
      </summary>
      <p>
        The finite dipole model uses the Lewin approach to approximate the
        effective polarizability of the tip in proximity to the sample
        surface. It captures retardation effects and lateral field
        distributions more accurately than point dipole models.
      </p>
      <MathJax>
        {`
        \\[
        Z(\\phi) = z_0 + z_{amp} (1 + \\cos \\phi)
        \\]`}
      </MathJax>
      <p>
        The effective polarizability{" "}
        {"\\( \\eta(\\omega) \\)"} is calculated using:
      </p>
      <MathJax>
        {`
        \\[
        \\eta(\\phi, \\beta) =
        \\frac{\\beta (2 L g - 2 Z - W_0 - a) \\ln \\left( \\frac{4L}{4Z + 2 W_0 + R} \\right)}{4L \\ln(4L/R) - \\beta (4 L g - 4 Z - 3a) \\ln \\left( \\frac{4L}{4Z + 2 R} \\right)}
        \\]`}
      </MathJax>
      <p>
        Here{" "}
        {"\\( \\beta = \\frac{\\varepsilon - 1}{\\varepsilon + 1} \\)"} and{" "}
        {"\\( W_0 = 1.31 a \\)"}, with {"\\( g = 0.7 e^{0.06i} \\)"}.
      </p>
      <p>
        The signal is then constructed from the harmonic component of the
        complex-valued near-field response:
      </p>
      <MathJax>
        {`
        \\[
        S(\\omega) = \\left| 
        \\frac{
          \\eta_{sam} (1 + C_f e^{-i 2 \\pi \\omega L \\cos \\alpha} r_p^{sam})
        }{
          \\eta_{ref} (1 + C_f e^{-i 2 \\pi \\omega L \\cos \\alpha} r_p^{ref})
        }
        \\right|
        \\quad , \\quad
        P(\\omega) = \\arg(\\dots)
        \\]`}
      </MathJax>
      <p>
        The reflection coefficients are computed using angle-dependent
        Fresnel expressions:
      </p>
      <MathJax>
        {`
        \\[
        r_p(\\varepsilon, \\alpha) = 
        \\frac{\\varepsilon \\cos \\alpha - \\sqrt{\\varepsilon - \\sin^2 \\alpha}}{
          \\varepsilon \\cos \\alpha + \\sqrt{\\varepsilon - \\sin^2 \\alpha}
        }
        \\]`}
      </MathJax>
    </details>

    <p style={{ marginTop: "1.5rem" }}>
      <a href="#top">↑ Back to top</a>
    </p>
    <hr />
  </MathJax>
</section>

{/* Gas Absorption / Emission Section */}
<MathJax>
  <section id="Gasmodel" style={{ marginTop: "3rem", marginBottom: "3rem" }}>
    <h3>&quot;Gas absorption spectroscopy&quot;: Absorption / Emission with Voigt line shapes</h3>
    <h4>
      &#8594; Fit <strong>gas absorption</strong> and <strong>gas emission</strong> spectra using a physics-based Voigt model.
    </h4>

    {/* Model Description */}
    <p>
      This model describes gas-phase spectral lines as a sum of <strong>Voigt profiles</strong>, i.e. a convolution
      of a Lorentzian (pressure / collision broadening) and a Gaussian (Doppler broadening). It can be used to fit:
    </p>
    <ul>
      <li>
        <strong>Absorption spectra</strong> (e.g., transmission-derived absorbance)
      </li>
      <li>
        <strong>Emission spectra</strong> (e.g., thermal or plasma emission lines)
      </li>
    </ul>

    <p>
      Each spectral line is defined by four parameters: center frequency {"\\(\\omega_0\\)"}, amplitude {"\\(A\\)"},
      Lorentzian width {"\\(\\gamma_L\\)"}, and Gaussian width {"\\(\\gamma_D\\)"}.
      In addition, the model includes a <strong>global scaling factor</strong> that multiplies all line amplitudes,
      which can mimic changes in <strong>optical path length</strong> (tube length) or <strong>gas concentration</strong>.
    </p>

    <p>
      For <strong>absorption only</strong>, the model can optionally apply a baseline correction using a polynomial.
      The baseline can be applied in an <strong>additive</strong> or <strong>multiplicative</strong> way.
      (Baseline correction is intentionally not provided for emission, where the baseline is typically instrument- and
      source-dependent and should be handled separately in preprocessing.)
    </p>

    {/* Inputs Table */}
    <h4>Inputs</h4>
    <table className="manual-table">
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Description</th>
          <th>Units</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{"\\( \\omega \\)"}</td>
          <td>Optical frequency axis of the measured spectrum</td>
          <td>{"\\( \\text{cm}^{-1} \\)"}</td>
        </tr>
        <tr>
          <td>{"\\( \\omega_0 \\)"}</td>
          <td>Line center position (one per spectral line)</td>
          <td>{"\\( \\text{cm}^{-1} \\)"}</td>
        </tr>
        <tr>
          <td>{"\\( A \\)"}</td>
          <td>Line amplitude (one per spectral line). Multiplied by the global scaling factor.</td>
          <td>arb. units</td>
        </tr>
        <tr>
          <td>{"\\( \\gamma_L \\)"}</td>
          <td>Lorentzian width parameter (pressure / collision broadening)</td>
          <td>{"\\( \\text{cm}^{-1} \\)"}</td>
        </tr>
        <tr>
          <td>{"\\( \\gamma_D \\)"}</td>
          <td>Gaussian width parameter (Doppler broadening)</td>
          <td>{"\\( \\text{cm}^{-1} \\)"}</td>
        </tr>
        <tr>
          <td>Global scaling</td>
          <td>
            Multiplies all line amplitudes: {"\\( A_i \\rightarrow s \\cdot A_i \\)"}.
            Can mimic tube length or concentration (single factor for now).
          </td>
          <td>unitless</td>
        </tr>
        <tr>
          <td>Baseline polynomial (absorption only)</td>
          <td>
            Polynomial baseline {"\\( B(\\omega) \\)"} used only for absorption. Degree and coefficients are user-defined.
          </td>
          <td>arb. units</td>
        </tr>
        <tr>
          <td>Baseline mode (absorption only)</td>
          <td>
            Additive or multiplicative baseline application (see formula below).
          </td>
          <td>-</td>
        </tr>
      </tbody>
    </table>

    {/* Outputs Table */}
    <h4>Outputs</h4>
    <table className="manual-table">
      <thead>
        <tr>
          <th>Output</th>
          <th>Description</th>
          <th>Units</th>
          <th>Mode</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Absorption spectrum</td>
          <td>Simulated / fitted absorption signal including optional baseline correction</td>
          <td>arb. units</td>
          <td>Absorption</td>
        </tr>
        <tr>
          <td>Emission spectrum</td>
          <td>Simulated / fitted emission signal (no baseline correction)</td>
          <td>arb. units</td>
          <td>Emission</td>
        </tr>
        <tr>
          <td>Fitted parameters</td>
          <td>
            {"\\( \\omega_0,\\ A,\\ \\gamma_L,\\ \\gamma_D \\)"} for each line, plus global scaling{" "}
            {"\\( s \\)"} and (optionally) polynomial baseline coefficients.
          </td>
          <td>varies</td>
          <td>All</td>
        </tr>
      </tbody>
    </table>

    {/* Use Cases */}
    <h4>Use Cases</h4>
    <ul>
      <li>Fit isolated or overlapping gas lines where both Doppler and pressure broadening matter</li>
      <li>Track concentration/path-length changes via the global scaling factor</li>
      <li>Quantify line positions and widths for gas identification or monitoring</li>
      <li>Absorption workflows where a smooth baseline drift must be corrected (absorption only)</li>
    </ul>

    {/* Experiment Setup */}
    <h4>Experiment Setup</h4>
    <ul>
      <li>
        <strong>Absorption:</strong> Import your absorption-like spectrum (e.g. absorbance or -ln(T)).
        If your spectrum has baseline drift, enable the polynomial baseline correction and start with a low degree
        (typically 1–3) before increasing complexity.
      </li>
      <li>
        <strong>Emission:</strong> Import your emission spectrum and model it as a sum of Voigt lines. If you have a
        strong background (instrument response, blackbody tail, fluorescence), correct it during preprocessing
        rather than with this model.
      </li>
    </ul>

    {/* Points of Attention */}
    <h4>Points of Attention</h4>
    <ul>
      <li>
        <strong>Parameter correlations:</strong> {"\\(A\\)"}, {"\\(\\gamma_L\\)"}, and {"\\(\\gamma_D\\)"} can trade off,
        especially for noisy or strongly overlapping lines. Use sensible bounds and avoid freeing everything at once.
      </li>
      <li>
        <strong>Baseline (absorption only):</strong> A high-degree polynomial can start fitting real spectral structure.
        Prefer the lowest degree that removes slow drift.
      </li>
      <li>
        <strong>Scaling factor meaning:</strong> The global scaling factor is a proxy for path length or concentration,
        but it will also absorb any global calibration mismatch in your intensity scaling.
      </li>
      <li>
        <strong>Units:</strong> Ensure your frequency axis is in {"\\(\\text{cm}^{-1}\\)"} to match Speqqle’s unit system.
      </li>
    </ul>

    {/* Advanced Physics */}
    <details style={{ marginTop: "1rem" }}>
      <summary>
        <strong>Advanced: Underlying Physics</strong>
      </summary>

      <p>
        Each line is modeled as a Voigt profile {"\\( V(\\omega; \\omega_0, \\gamma_L, \\gamma_D) \\)"}, i.e. the
        convolution of a Lorentzian {"\\(L\\)"} and a Gaussian {"\\(G\\)"}:
      </p>

      <p style={{ marginLeft: "1em" }}>
        {"\\[ V(\\omega) = (L * G)(\\omega) \\]"}
      </p>

      <p>
        The total signal is a sum of lines with amplitudes {"\\(A_i\\)"}, multiplied by a global scaling factor {"\\(s\\)"}:
      </p>

      <p style={{ marginLeft: "1em" }}>
        {"\\[ S_0(\\omega) = \\sum_i (s\\,A_i)\\, V(\\omega; \\omega_{0,i}, \\gamma_{L,i}, \\gamma_{D,i}) \\]"}
      </p>

      <p>
        For absorption, an optional polynomial baseline {"\\( B(\\omega) \\)"} can be applied either additively or
        multiplicatively:
      </p>

      <p style={{ marginLeft: "1em" }}>
        {"\\[ \\text{Additive:}\\quad S(\\omega) = S_0(\\omega) + B(\\omega) \\]"}
      </p>
      <p style={{ marginLeft: "1em" }}>
        {"\\[ \\text{Multiplicative:}\\quad S(\\omega) = S_0(\\omega) \\cdot \\bigl(1 + B(\\omega)\\bigr) \\]"}
      </p>

      <p>
        <strong>Roadmap:</strong> today, {"\\(s\\)"} is a single global factor. In a future update, Speqqle will allow
        multiple scaling factors applied to <em>groups</em> of lines, enabling mixtures of gases with different
        concentrations.
      </p>
    </details>

    {/* Back to Top */}
    <p style={{ marginTop: "1.5rem" }}>
      <a href="#top">↑ Back to top</a>
    </p>

    <hr />
  </section>
</MathJax>

{/* THz Time-Domain Spectroscopy Section */}
<MathJax>
  <section
    id="THzmodel"
    style={{ marginTop: "3rem", marginBottom: "3rem" }}
  >
    <h3>
      &quot;TeraHertz time-domain spectroscopy&quot;: TD sample, TD, and Fourier
    </h3>
    <h4>
      &#8594; Fit <strong>THz time-domain data</strong> using a physics-based
      multilayer model with explicit time–frequency separation.
    </h4>

    {/* Model Description */}
    <p>
      THz time-domain spectroscopy (THz-TDS) in Speqqle is implemented as a
      <strong> three-step modeling workflow</strong> that mirrors real
      experiments:
    </p>

    <ol>
      <li>
        <strong>TD sample</strong>: defines the physical sample (materials,
        thicknesses, polarization, angle of incidence) and computes its
        <strong> frequency-domain optical response</strong>.
      </li>
      <li>
        <strong>TD</strong>: converts the TD sample response into the
        <strong> time domain</strong>, optionally using a reference pulse.
      </li>
      <li>
        <strong>Fourier</strong>: Fourier-transforms time-domain data into the
        <strong> frequency domain</strong> for inspection and model building.
      </li>
    </ol>

    <p>
      This strict separation avoids mixing material physics, signal processing,
      and referencing, and keeps THz fits interpretable and robust.
    </p>

    {/* Inputs */}
    <h4>Inputs</h4>
    <table className="manual-table">
      <thead>
        <tr>
          <th>Model</th>
          <th>Key Inputs</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>TD sample</strong></td>
          <td>
            θ, polarization, layer stack, material models
          </td>
          <td>
            Defines the multilayer sample and computes complex
            {"\\( R(\\omega),\\ T(\\omega) \\)"}.
          </td>
        </tr>
        <tr>
          <td><strong>TD</strong></td>
          <td>
            TD sample, DataRef, RF, {"\\( \\omega_{min}, \\omega_{max} \\)"}
          </td>
          <td>
            Generates time-domain signals from the TD sample response.
          </td>
        </tr>
        <tr>
          <td><strong>Fourier</strong></td>
          <td>
            DataSam, DataRef, RF
          </td>
          <td>
            Converts time-domain data into frequency-domain spectra.
          </td>
        </tr>
      </tbody>
    </table>

    {/* Outputs */}
    <h4>Outputs</h4>
    <table className="manual-table">
      <thead>
        <tr>
          <th>Model</th>
          <th>Outputs</th>
          <th>Domain</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>TD sample</strong></td>
          <td>
            Reflectivity, Transmission,{" "}
            {"\\( r_{real}, r_{imag}, t_{real}, t_{imag} \\)"}
          </td>
          <td>Frequency</td>
        </tr>
        <tr>
          <td><strong>TD</strong></td>
          <td>
            {"\\( E_{sam}(t), E_{ref}(t), I_{sam}(\\omega), I_{ref}(\\omega) \\)"}
          </td>
          <td>Time (primary)</td>
        </tr>
        <tr>
          <td><strong>Fourier</strong></td>
          <td>
            {"\\( I_{sam}(\\omega), I_{ref}(\\omega), I_{sam}/I_{ref} \\)"}
          </td>
          <td>Frequency</td>
        </tr>
      </tbody>
    </table>

    {/* Use Cases */}
    <h4>Use Cases</h4>
    <ul>
      <li>
        Fit measured THz time-domain transmission or reflection data
      </li>
      <li>
        Extract dielectric functions and thicknesses of bulk samples,
        thin films, or multilayers
      </li>
      <li>
        Analyze pulse delays, echoes, and internal reflections
      </li>
      <li>
        Build and validate material models in the frequency domain using
        Fourier-transformed data
      </li>
    </ul>

    {/* Points of Attention */}
    <h4>Points of Attention</h4>
    <ul>
      <li>
        <strong>TD sample is frequency-domain only.</strong> It does not
        produce time traces by itself.
      </li>
      <li>
        <strong>TD outputs are time-domain.</strong> This is the model to
        compare directly to measured THz pulses.
      </li>
      <li>
        <strong>DataRef</strong> should represent the bare reference pulse
        (air, empty substrate, or system response).
      </li>
      <li>
        <strong>RF</strong> controls sampling density: higher RF reduces
        computation time;  RF &lt; 1 increases apparent frequency resolution
        without adding new information.
      </li>
    </ul>

    {/* Advanced */}
    <details style={{ marginTop: "1rem" }}>
      <summary>
        <strong>Advanced: Internal Logic</strong>
      </summary>
      <p>
        The TD sample computes complex frequency-domain coefficients. The TD
        model applies windowing and inverse Fourier transforms to generate
        time-domain signals, optionally normalized by a reference pulse.
      </p>
      <p>
        The Fourier model performs forward Fourier transforms on measured or
        simulated time traces, enabling frequency-domain inspection and
        material model development.
      </p>
    </details>

    {/* Back to Top */}
    <p style={{ marginTop: "1.5rem" }}>
      <a href="#top">↑ Back to top</a>
    </p>

    <hr />
  </section>
</MathJax>

      <MathJax>
        <section
          id="diffmodel"
          style={{ marginTop: "3rem", marginBottom: "3rem" }}
        >
          <h3>&quot;Differential R/T&quot;: Small-signal Reflectivity / Transmission</h3>
          <h4>
            &#8594; Fit <strong>small changes</strong> in reflectivity and transmission
            (e.g. pump–probe, bias, temperature steps) using a layer-resolved
            perturbation {"\\( \\Delta\\varepsilon(\\omega) \\)"}.
          </h4>

          <p>
            The Differential R/T model is designed for experiments where the
            <strong> measured observable is a small change </strong> relative to a
            reference state, for example:
            {" \\( \\Delta R(\\omega) = R(\\omega,t) - R(\\omega,t_0)  \\) "}
             and the commonly plotted quantity
            {" \\( \\Delta R(\\omega)/R(\\omega,t_0) \\) "}.
          </p>

          <p>
            The Differential R/T model first computes the optical response of a 
            <strong> reference multilayer stack </strong>, using the same physical framework
            as the isotropic R/T model. For each layer, the user can then assign a
            <strong> perturbation material model</strong>, which represents a
            <strong> small change in the dielectric function </strong>
            {"\\( \\Delta\\varepsilon(\\omega) \\)"} of that layer.
          </p>

          <p>
            Based on this perturbation, the model predicts the resulting changes in
            reflectivity and transmission, {" \\( \\Delta R \\)"} and {"\\( \\Delta T \\)"},
            as well as their normalized counterparts.  
            <strong> Ellipsometric observables are not computed</strong> in this model.
          </p>

          <p>
            Although a reference dielectric function is required, the extracted 
             {" \\(  \\Delta\\varepsilon(\\omega) \\)"} is only weakly sensitive to its exact
            value. Instead of recomputing the full optical response for the perturbed
            system, the model uses a <strong> first-order (linear) approximation</strong>,
            valid for sufficiently small dielectric changes.
          </p>
          
          <p>
            This linearization makes the Differential R/T model robust against
            uncertainties in the reference dielectric function and reduces the dependence
            on precise prior knowledge of the unperturbed material, while still capturing
            the physically meaningful spectral signatures of the perturbation.
          </p>

          {/* What it can / cannot do */}
          <h4>What this model can and cannot do</h4>
          <ul>
            <li>
              <strong>Can:</strong> fit small, layer-specific optical changes using a
              linear (first-order) approximation around a reference stack.
            </li>
            <li>
              <strong>Can:</strong> model multiple perturbed layers simultaneously, each
              with its own {"\\( \\Delta\\varepsilon(\\omega) \\)"}.
            </li>
            <li>
              <strong>Cannot:</strong> replace the reference stack with a new full optical
              stack (perturbations are not additional layers).
            </li>
            <li>
              <strong>Cannot:</strong> describe large changes where fringes shift strongly
              or where the response is highly nonlinear in{" "}
              {"\\( \\varepsilon(\\omega) \\)"}.
            </li>
            <li>
              <strong>Cannot:</strong> compute {"\\( \\Psi \\)"} and {"\\( \\Delta \\)"} (no ellipsometry outputs here).
            </li>
          </ul>

          {/* Inputs */}
          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Reference stack materials</td>
                <td>
                  Each layer references a <strong>Material submodel</strong> defining
                  the reference dielectric function {"\\( \\varepsilon_{ref}(\\omega) \\)"}.
                </td>
                <td>-</td>
              </tr>
              <tr>
                <td>Perturbation models (per layer)</td>
                <td>
                  Optional per-layer model defining{" "}
                  {"\\( \\Delta\\varepsilon(\\omega) = \\Delta\\varepsilon_1(\\omega) + i\\,\\Delta\\varepsilon_2(\\omega) \\)"}.
                  This is the <strong>small step</strong> around the reference state.
                </td>
                <td>-</td>
              </tr>
              <tr>
                <td>Layer thickness {"\\( d \\)"}</td>
                <td>Thickness of each layer in the reference stack. Currently not fittable. </td>
                <td>cm</td>
              </tr>
              <tr>
                <td>Coherence (&quot;coh&quot;)</td>
                <td>Controls interference treatment (coherent vs. incoherent).</td>
                <td>1 / 0</td>
              </tr>
              <tr>
                <td>Angle of incidence {"\\( \\theta \\)"}</td>
                <td>Incident angle of the beam on the sample. Not fittable. </td>
                <td>degrees</td>
              </tr>
              <tr>
                <td>Polarizer angle (&quot;pol&quot;)</td>
                <td>Angle of linear polarization (same convention as isotropic R/T). Not fittable. </td>
                <td>degrees</td>
              </tr>
            </tbody>
          </table>

          {/* Outputs */}
          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
                <th>Fittable </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( R_{ref}(\\omega) \\)"}</td>
                <td>
                  Reflectivity of the <strong>reference stack only</strong>.
                </td>
                <td>unitless (0–1)</td>
                <td>No (diagnostic)</td>
              </tr>
              <tr>
                <td>{"\\( \\Delta R(\\omega) \\)"}</td>
                <td>
                  Differential reflectivity caused by all layer perturbations.
                </td>
                <td>unitless</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>{"\\( \\Delta R(\\omega)/R_{ref}(\\omega) \\)"}</td>
                <td>
                  Relative reflectivity change (most common experimental observable).
                </td>
                <td>unitless</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>{"\\( T_{ref}(\\omega) \\)"}</td>
                <td>
                  Transmission of the <strong>reference stack only</strong>.
                </td>
                <td>unitless (0–1)</td>
                <td>No (diagnostic)</td>
              </tr>
              <tr>
                <td>{"\\( \\Delta T(\\omega) \\)"}</td>
                <td>Differential transmission caused by all layer perturbations.</td>
                <td>unitless</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td>{"\\( \\Delta T(\\omega)/T_{ref}(\\omega) \\)"}</td>
                <td>Relative transmission change.</td>
                <td>unitless</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>

          <h4>Interpreting Differential Material Outputs</h4>

          <p>
         In addition to differential optical observables such as
          {" \\( \\Delta R(\\omega) \\)"}, {" \\( \\Delta T(\\omega) \\)"},
          and their normalized counterparts {" \\( \\Delta R / R \\)"} and
          {" \\( \\Delta T / T \\)"},
          the Differential R/T model also computes and exposes
          <strong> layer-resolved differential material quantities</strong>.
          </p>

          <p>
              These quantities appear in the <em>Curves List</em> and can be grouped into
              <strong> two distinct categories</strong>, corresponding to different physical meanings
              and levels of reliability.
          </p>
          
          <h5>1. Differential material properties (recommended)</h5>

            <p>
              For each perturbed layer, the corresponding material model outputs the dielectric and optical
              quantities originating directly from the <em>perturbation material model</em>:
            </p>

            <ul>
              <li>{"\\( \\Delta\\varepsilon(\\omega) \\)"} (real and imaginary parts)</li>
              <li>{"\\( \\Delta n(\\omega) \\)"} and {"\\( \\Delta k(\\omega) \\)"}</li>
            </ul>

            <p>
              These curves represent the <strong>intrinsic material change</strong> introduced by the
              perturbation and are the quantities that are directly inferred by the
              differential model. Because they are obtained through a linear (first-order)
              approximation, they are only weakly sensitive to uncertainties in the
              reference dielectric function.
            </p>

            <p>
              <strong>
                These differential quantities are the most reliable material outputs of the
                Differential R/T model and should be used for quantitative interpretation.
              </strong>
            </p>

            <h5>2. Effective material properties (reference&nbsp;+&nbsp;perturbation)</h5>

            <p>
              The model also reports effective material properties for each perturbed layer:
            </p>

            <ul>
              <li>{"\\( \\varepsilon(\\omega) = \\varepsilon_{\\mathrm{ref}}(\\omega) + \\Delta\\varepsilon(\\omega) \\)"}</li>
              <li>{"\\( n(\\omega) \\)"} and {"\\( k(\\omega) \\)"} derived from this total dielectric function</li>
            </ul>

              <p>
                These outputs represent the <strong>sum of the reference material and the
                differential perturbation</strong>. While they can be useful for visualization
                or qualitative comparison, their accuracy is inherently limited by the
                accuracy of the reference dielectric function.
              </p>

              <p>
                Any uncertainty or modeling error in the reference stack propagates directly
                into these total {"\\( \\varepsilon \\)"}, {"\\( n \\)"}, and {"\\( k \\)"} curves.
                As a result, they are generally <strong>less reliable than the differential
                quantities alone</strong>, especially when the reference material is not known
                with high precision.
              </p>

              <p>
                <strong>
                  For this reason, users are strongly encouraged to base their physical
                  interpretation on {"\\( \\Delta\\varepsilon \\)"}, {"\\( \\Delta n \\)"}, and
                  {" \\( \\Delta k \\)"} rather than on the absolute material properties.
                </strong>
              </p>

<h4>Small changes in material properties</h4>

<p>
  The Differential R/T model is based on a <strong>linear response (first-order)
  approximation</strong>. Changes in reflectivity and transmission are expressed
  as a weighted sum of changes in the complex dielectric function of each layer:
</p>

<p style={{ marginLeft: "1em" }}>
  {`
  \\[
  \\Delta R(\\omega)
  =
  \\sum_{\\text{layers}}
  \\left[
    \\left.
      \\frac{\\partial R}{\\partial \\varepsilon_1}
    \\right|_{\\varepsilon = \\varepsilon_{\\mathrm{ref}}}
    \\Delta \\varepsilon_1(\\omega)
    +
    \\left.
      \\frac{\\partial R}{\\partial \\varepsilon_2}
    \\right|_{\\varepsilon = \\varepsilon_{\\mathrm{ref}}}
    \\Delta \\varepsilon_2(\\omega)
  \\right]
  \\]
  `}
</p>


<p>
  This means that:
</p>

<ul>
  <li>
    The <strong>primary fitted quantities</strong> are{" "}
    {"\\( \\Delta \\varepsilon_1(\\omega) \\)"} and{" "}
    {"\\( \\Delta \\varepsilon_2(\\omega) \\)"}.
  </li>
  <li>
    Changes in{" "}
    {"\\( R(\\omega) \\)"} and{" "}
    {"\\( T(\\omega) \\)"} are <em>derived consequences</em> of these perturbations.
  </li>
  <li>
    {"\\( \\Delta n \\)"} and{" "}
    {"\\( \\Delta k \\)"} are computed as derived quantities from{" "}
    {"\\( \\Delta \\varepsilon \\)"} using a linearized relation.
  </li>
</ul>

<p>
  To first order:
</p>

<p style={{ marginLeft: "1em" }}>
  {"\\[ \\Delta n + i\\,\\Delta k \\;\\approx\\; \\frac{1}{2\\sqrt{\\varepsilon_{\\text{ref}}}} \\, \\Delta \\varepsilon \\]"}
</p>


<h4>Connection to experimentally measured signals</h4>

<p>
  In many experiments, the measured quantity is a <strong>relative change</strong>,
  for example:
</p>

<p style={{ marginLeft: "1em" }}>
  {"\\[ \\frac{R(t) - R(t_0)}{R(t_0)} \\]"}
</p>

<p>
  In Speqqle, this corresponds at first order to the model output{" "}
  {"\\( \\Delta R / R_{\\text{ref}} \\)"}.
</p>

<p>
  Crucially, this quantity is <strong>directly linked</strong> to the differential
  dielectric function:
</p>

<p style={{ marginLeft: "1em" }}>
  {"\\[ \\frac{\\Delta R}{R_{\\text{ref}}} \\;\\longleftrightarrow\\; \\Delta \\varepsilon_1(\\omega),\\; \\Delta \\varepsilon_2(\\omega) \\]"}
</p>

<p>
  As a result, plotting{" "}
  {"\\( \\Delta \\varepsilon \\)"} or{" "}
  {"\\( \\Delta n, \\Delta k \\)"} provides direct physical insight into the origin
  of the measured differential optical signal.
</p>

<h4>Practical interpretation guidelines</h4>

<ul>
  <li>
    Smooth{" "}
    {"\\( \\Delta \\varepsilon_1 \\)"} or{" "}
    {"\\( \\Delta n \\)"} typically indicate dispersive effects (e.g. density changes,
    strain, refractive index shifts).
  </li>
  <li>
    Peak-like{" "}
    {"\\( \\Delta \\varepsilon_2 \\)"} or{" "}
    {"\\( \\Delta k \\)"} indicate absorptive changes (e.g. increased damping,
    photo-induced carriers, new excitations).
  </li>
  <li>
    Layer-specific perturbations allow localization of the physical change within
    a multilayer stack.
  </li>
</ul>


<h4>Validity and limitations</h4>

<ul>
  <li>
    The linear approximation is valid only when{" "}
    {"\\( |\\Delta \\varepsilon| \\ll |\\varepsilon_{\\text{ref}}| \\)"}.
  </li>
  <li>
    If perturbations become large, the model may still fit numerically but will
    no longer represent a physically correct first-order response.
  </li>
  <li>
    This model is not intended for large structural changes, phase transitions
    with strong dielectric contrast, and may become inaccurate if the reference is too poorly defined.
  </li>
</ul>

<p>
  <strong>In short:</strong> the Differential R/T model fits{" "}
  <em>changes in material properties</em>, not absolute optical constants, and the
  exposed dielectric and refractive-index curves represent the true physical
  content of the fit.
</p>


          {/* Use cases */}
          <h4>Use Cases</h4>
          <ul>
            <li>
              Pump–probe / photo-excitation: fit {"\\( \\Delta R/R \\)"} or {"\\( \\Delta T/T \\)"} to extract{" "}
              {"\\( \\Delta\\varepsilon(\\omega) \\)"} of a specific layer.
            </li>
            <li>
              Bias/field/temperature steps: quantify small optical-property changes while keeping the reference stack fixed.
            </li>
            <li>
              Separate contributions from different layers by assigning separate perturbation models.
            </li>
            <li>
              Robust fitting when the absolute reference spectrum is known but imperfectly calibrated in intensity.
            </li>
          </ul>

          {/* UI interpretation */}
          <h4>How to interpret the UI (important)</h4>
          <ul>
            <li>
              The <strong>Reference</strong> column defines the real optical stack (materials + thicknesses).
            </li>
            <li>
              The <strong>Perturbation</strong> entry for a layer is{" "}
              <strong>not an added layer</strong>. It is a model for{" "}
              {"\\( \\Delta\\varepsilon(\\omega) \\)"} applied to that existing layer.
            </li>
            <li>
              If a layer has no perturbation, the epsilon infinity of that layer = 0 and oscillators set to 0. Then it contributes{" "}
              {"\\( \\Delta\\varepsilon = 0 \\)"} for that layer.
            </li>
            <li>
              The model always outputs {"\\( R_{ref} \\)"} and {"\\( T_{ref} \\)"} as diagnostics, but fits are typically performed on{" "}
              {"\\( \\Delta R \\)"}, {"\\( \\Delta R/R_{ref} \\)"}, {"\\( \\Delta T \\)"}, or {"\\( \\Delta T/T_{ref} \\)"}.
            </li>
          </ul>

          {/* Worked example */}
          <h4>Worked example</h4>
          <p>
            Consider a two-layer sample (thin film on substrate). The reference stack is:
          </p>
          <ul>
            <li>
              Layer 1: Si{"\\( _3 \\)"}N{"\\( _4 \\)"} film (coherent) with {"\\( \\varepsilon_{ref,1}(\\omega) \\)"} and thickness {"\\( d_1 \\)"}.
            </li>
            <li>
              Layer 2: SiO{"\\( _2 \\)"} substrate (often incoherent) with {"\\( \\varepsilon_{ref,2}(\\omega) \\)"} and thickness {"\\( d_2 \\)"}.
            </li>
          </ul>
          <p>
            If only the film is photo-excited, you assign a perturbation model to layer 1:
            {" \\( \\Delta\\varepsilon_1(\\omega) \\neq 0 \\)"} and keep layer 2 unperturbed:
            {" \\( \\Delta\\varepsilon_2(\\omega)=0 \\)"}. The model then predicts the measured
            {" \\( \\Delta R/R \\)"} and {"\\( \\Delta T/T \\)"} without requiring you to refit the entire absolute spectrum at each time step.
          </p>

          {/* Keep perturbations small */}
          <h4>How to keep perturbations small (so the model stays valid)</h4>
          <ul>
            <li>
              Start by fitting the <strong>reference stack</strong> first (thicknesses and{" "}
              {"\\( \\varepsilon_{ref}(\\omega) \\)"}), before freeing any perturbation parameters and fixing the reference stack. Or start by getting a reasonable reference model from literature. 
            </li>
            <li>
              Use physically small amplitudes for perturbation oscillators; impose bounds so that{" "}
              {"\\( |\\Delta\\varepsilon(\\omega)| \\ll |\\varepsilon_{ref}(\\omega)| \\)"} across the fitted spectral window.
            </li>
            <li>
              A practical rule of thumb: ensure{" "}
              {"\\( |\\Delta R(\\omega)| \\ll R_{ref}(\\omega) \\)"} and{" "}
              {"\\( |\\Delta T(\\omega)| \\ll T_{ref}(\\omega) \\)"} for most of the spectrum.
            </li>
            <li>
              If fringe positions shift strongly (phase shift of Fabry–Pérot oscillations), the perturbation is likely too large for a strict linear approximation.
            </li>
          </ul>

          {/* Common mistakes */}
          <h4>Common mistakes</h4>
          <ul>
            <li>
              Interpreting the perturbation model as a full dielectric function. It is{" "}
              <strong>only</strong> {"\\( \\Delta\\varepsilon(\\omega) \\)"}.
            </li>
            <li>
              Allowing very large perturbation amplitudes: the fit may look good but becomes non-physical and non-unique.
            </li>
            <li>
              Trying to compensate an incorrect reference stack by fitting large{" "}
              {"\\( \\Delta\\varepsilon \\)"}. Always fix the reference first.
            </li>
            <li>
              Using {"\\( \\Delta R/R \\)"} while your experimental normalization is actually{" "}
              {"\\( (R(t)-R(t_0))/R(t) \\)"} (see mapping below).
            </li>
          </ul>

          {/* Points of attention */}
          <h4>Points of Attention</h4>
          <ul>
            <li>
              The most robust workflow is to fit{" "}
              {"\\( \\Delta R/R_{ref} \\)"} or{" "}
              {"\\( \\Delta T/T_{ref} \\)"} when your experiment provides relative changes.
            </li>
            <li>
              If your reference state {"\\( t_0 \\)"} is uncertain, fitting absolute{" "}
              {"\\( \\Delta R \\)"} can become ambiguous. Consider constraining the reference stack more tightly.
            </li>
            <li>
              Units: thickness in cm, frequency in{" "}
              {"\\( \\text{cm}^{-1} \\)"}, angles in degrees.
            </li>
          </ul>

          {/* Advanced physics */}
          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics & equations</strong>
            </summary>

            <p>
              The reference response is computed from the reference multilayer stack:
              {"\\( R_{ref}(\\omega) \\)"} and {"\\( T_{ref}(\\omega) \\)"}.
            </p>

            <p>
              Each perturbed layer contributes a small dielectric change:
              {"\\( \\Delta\\varepsilon(\\omega) = \\Delta\\varepsilon_1(\\omega) + i\\,\\Delta\\varepsilon_2(\\omega) \\)"}.
              Speqqle computes first-order changes via:
            </p>

            <MathJax>
              {`
              \\[
              \\Delta R(\\omega)
              \\approx
              \\sum_{\\text{layers } \\ell}
              \\left(
                \\frac{\\partial R_{ref}(\\omega)}{\\partial \\varepsilon_{1,\\ell}}\\,\\Delta\\varepsilon_{1,\\ell}(\\omega)
                +
                \\frac{\\partial R_{ref}(\\omega)}{\\partial \\varepsilon_{2,\\ell}}\\,\\Delta\\varepsilon_{2,\\ell}(\\omega)
              \\right)
              \\]
              `}
            </MathJax>

            <MathJax>
              {`
              \\[
              \\Delta T(\\omega)
              \\approx
              \\sum_{\\text{layers } \\ell}
              \\left(
                \\frac{\\partial T_{ref}(\\omega)}{\\partial \\varepsilon_{1,\\ell}}\\,\\Delta\\varepsilon_{1,\\ell}(\\omega)
                +
                \\frac{\\partial T_{ref}(\\omega)}{\\partial \\varepsilon_{2,\\ell}}\\,\\Delta\\varepsilon_{2,\\ell}(\\omega)
              \\right)
              \\]
              `}
            </MathJax>

            <p>
              The normalized outputs are:
              {"\\( \\Delta R/R_{ref} \\)"} and {"\\( \\Delta T/T_{ref} \\)"}.
            </p>

            <h4>Mapping to common experimental observables</h4>
            <p>
              Many experiments measure a relative change between a time{" "}
              {"\\( t \\)"} and a reference time {"\\( t_0 \\)"}:
            </p>

            <MathJax>
              {`
              \\[
              \\frac{R(t) - R(t_0)}{R(t_0)}
              =
              \\frac{\\Delta R}{R_{ref}}
              \\quad\\text{if } R_{ref} = R(t_0)
              \\]
              `}
            </MathJax>

            <p>
              Sometimes the normalization uses {"\\( R(t) \\)"} in the denominator:
            </p>
            <MathJax>
              {`
              \\[
              \\frac{R(t) - R(t_0)}{R(t)}
              =
              \\frac{\\Delta R}{R(t_0) + \\Delta R}
              \\approx
              \\frac{\\Delta R}{R(t_0)}
              \\quad\\text{when } |\\Delta R| \\ll R(t_0)
              \\]
              `}
            </MathJax>

            <p>
              This shows why {"\\( \\Delta R/R_{ref} \\)"} is the natural model output:
              it matches most experimental normalizations in the small-signal limit.
            </p>

            <h4>About the non-linear alternative</h4>
            <p>
              In principle, one could compute:
              {"\\( \\Delta R = R(\\varepsilon_{ref}+\\Delta\\varepsilon) - R(\\varepsilon_{ref}) \\)"}.
              This does not require a linear approximation, but it becomes{" "}
              <strong>more sensitive</strong> to errors in the reference model. However should be used when{" "}
              {"\\( \\Delta\\varepsilon \\)"} is not small.
            </p>

            <h4>Recommended validity check</h4>
            <p>
              After fitting, verify that:
              {"\\( |\\Delta R| \\ll R_{ref} \\)"} and {"\\( |\\Delta T| \\ll T_{ref} \\)"}.
              If this is not true, the problem is likely outside the strict linear regime.
            </p>
          </details>

          {/* Back to Top */}
          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr/>
        </section>
      </MathJax>

      <h2>What If the Experiment You Need Is Not Listed?</h2>
      <p>If the experiment you need is not yet included, you can:</p>
      <ul>
        <li>
          Check if it&apos;s available in a{" "}
          <Link to="../packages/overview">special package</Link>.
        </li>
        <li>
          Request it via the <strong>Feedback Form</strong> in your{" "}
          Profile.
        </li>
        <li>Email the Speqqle team with your use case.</li>
      </ul>

      <h2>Next Steps</h2>
      <ul>
        <li>
        <Link to="../experiments/loadmodels">Load a saved experiment or submodel</Link>.
        </li>
        <li>
        <Link to="../experiments/materialmodels">Edit or extend your material model</Link>.
        </li>
        <li>
        <Link to="../parameters/editing">Edit or extend your material model</Link>.
        </li>
        <li>
        <Link to="../graphs/overview">Plot your simulated or fitted dat</Link>.
        </li>
      </ul>
    </ManualContent>
  );
};

export default ModelList;
